import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { EndpointService } from '../endpoints.service';
import {
  NryfEventResponse,
  NryfMasterdataResponse,
  NryfDisciplineResponse,
  NryfRegionResponse
} from '../models/response.module';
import { GetResponse } from '../models/global/response-wrapper.service';

@Injectable({
  providedIn: 'root'
})
export class NryfService {
  private masterdataCache: NryfMasterdataResponse | null = null;

  constructor(private endpointService: EndpointService) {}

  /**
   * Fetch NRYF events with optional filtering
   */
  getEvents(params?: {
    disciplineIds?: string[],
    regionIds?: string[],
    limit?: number,
    offset?: number
  }): Observable<GetResponse<NryfEventResponse[]>> {
    const apiParams: any = {};

    if (params?.disciplineIds?.length) {
      apiParams.discipline_ids = params.disciplineIds.join(',');
    }
    if (params?.regionIds?.length) {
      apiParams.region_ids = params.regionIds.join(',');
    }
    if (params?.limit) {
      apiParams.limit = params.limit;
    }
    if (params?.offset) {
      apiParams.offset = params.offset;
    }

    return this.endpointService.aqh_eve_200(apiParams);
  }

  /**
   * Fetch NRYF masterdata (disciplines, regions, competition levels)
   * Results are cached in memory for the session
   */
  getMasterdata(): Observable<GetResponse<NryfMasterdataResponse>> {
    if (this.masterdataCache) {
      return of({
        data: this.masterdataCache,
        status: 'success',
        reason: '',
        total_items: 1,
        asset_id: ''
      } as GetResponse<NryfMasterdataResponse>);
    }

    return this.endpointService.aqh_eve_201().pipe(
      tap(response => {
        if (response.data) {
          this.masterdataCache = response.data;
        }
      })
    );
  }

  /**
   * Fetch single NRYF event details
   */
  getEventDetails(eventId: string): Observable<GetResponse<NryfEventResponse>> {
    return this.endpointService.aqh_eve_202(eventId);
  }

  /**
   * Get discipline name by ID from cached masterdata
   */
  getDisciplineName(disciplineId: string): string {
    if (!this.masterdataCache) return '';
    const discipline = this.masterdataCache.disciplines.find(d => d.id === disciplineId);
    return discipline?.name || '';
  }

  /**
   * Get region name by ID from cached masterdata
   */
  getRegionName(regionId: string): string {
    if (!this.masterdataCache) return '';
    const region = this.masterdataCache.regions.find(r => r.id === regionId);
    return region?.name || '';
  }

  /**
   * Get competition level name by ID from cached masterdata
   */
  getCompetitionLevelName(levelId: string): string {
    if (!this.masterdataCache) return '';
    const level = this.masterdataCache.competitionLevels.find(l => l.id === levelId);
    return level?.name || '';
  }

  /**
   * Build the registration URL for nryfstevne.no
   */
  buildRegistrationUrl(eventId: string): string {
    return `https://nryfstevne.no/events/${eventId}`;
  }

  /**
   * Get all disciplines (from cache or fetch)
   */
  getDisciplines(): NryfDisciplineResponse[] {
    return this.masterdataCache?.disciplines || [];
  }

  /**
   * Get all regions (from cache or fetch)
   */
  getRegions(): NryfRegionResponse[] {
    return this.masterdataCache?.regions || [];
  }

  /**
   * Clear the masterdata cache
   */
  clearCache(): void {
    this.masterdataCache = null;
  }
}
